import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import dateFormat from "dateformat";

import { axiosBitrixInstance } from "@/resources/requestInfo";
import { transformBitrixDataToArenas, addExtraTimeSlots } from "@/utils/courtRent/transformBitrixData";
import { prepareCreateCancelRentData, CreateCancelRentRequest } from "@/utils/courtRent/createCancelRent";
import { getPartnerIdFromStorage } from "@/utils";
import { loggerSendingData } from "@/utils/courtRent/logger";
import { ISelctedSeat } from "./types";

import { IArena, IRacket } from "./types";

export const fetchGetActiveCourts = createAsyncThunk<any>(
  "rent/fetchGetActiveCourts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosBitrixInstance.get("/ActiveCourts/");
      return response.data;
    } catch (error: any) {
      console.log("error fetchGetActiveCourts >>>", error);
      return rejectWithValue(error.response?.data || "Ошибка получения активных кортов");
    }
  }
);

export const fetchGetArenas = createAsyncThunk<IArena[], { date: string; partnerId?: string }>(
  "rent/fetchGetArenas",
  async ({ date, partnerId }, { rejectWithValue, getState }) => {
    try {
      const formattedDate = dateFormat(date, "yyyy-mm-dd") + "T00:00:00";
      
      let finalPartnerId = partnerId || '';
      
      if (!finalPartnerId) {
        const storedPartnerId = getPartnerIdFromStorage();
        finalPartnerId = storedPartnerId || "d4fc8381-e486-11ea-80c3-ac1f6bb3f7d3";
      }
      const [activeCourtsResponse, scheduleResponse] = await Promise.all([
        axiosBitrixInstance.get("/ActiveCourts/"),
        axiosBitrixInstance.get(`/DailySchedule/${finalPartnerId}/${formattedDate}`),
      ]);

      const activeCourts = activeCourtsResponse.data?.activeCourts || [];
      const scheduleData = scheduleResponse.data?.schedule || scheduleResponse.data || [];

      const reversedSchedule = Array.isArray(scheduleData) ? [...scheduleData].reverse() : [];

      let arenas = transformBitrixDataToArenas(reversedSchedule, activeCourts, date);

      arenas = addExtraTimeSlots(arenas);

      return arenas;
    } catch (error: any) {
      console.log("error fetchGetArenas >>>", error);
      return rejectWithValue(error.response?.data || "Ошибка получения расписания");
    }
  }
);

export const fetchCreateCancelRent = createAsyncThunk<
  any,
  {
    newBasket: { [key: string]: ISelctedSeat };
    cancelBasket?: { [key: string]: ISelctedSeat };
    partnerId?: string;
    phone?: string;
  }
>(
  "rent/fetchCreateCancelRent",
  async ({ newBasket, cancelBasket = {}, partnerId, phone }, { rejectWithValue, getState }) => {
    const state = getState() as any;
    
    try {
      let finalPartnerId: string = partnerId || '';
      
      if (!finalPartnerId) {
        const storedPartnerId = getPartnerIdFromStorage();
        finalPartnerId = storedPartnerId || "";
      }

      const finalPhone: string = phone || 
        state.authAndReg?.user?.phone || 
        state.authAndReg?.phone || 
        "";

      const requestData = prepareCreateCancelRentData(
        newBasket,
        cancelBasket,
        finalPartnerId,
        finalPhone
      );

      const hasNewReserve = requestData.newReserve.length > 0;
      const hasCancelReserve = requestData.cancelReserve.length > 0;
      const logType = hasNewReserve && hasCancelReserve 
        ? 'newRentCourtSite'
        : hasNewReserve 
        ? 'newRentCourtSite' 
        : 'cancelRentCourtSite';

      try {
        await loggerSendingData({ 
          type: logType, 
          data: requestData,
          state 
        });
      } catch (logError) {
        console.warn('Logger error (non-critical) before request:', logError);
      }

      const response = await axiosBitrixInstance.post("/CreateCancelRent/", requestData);
      
      try {
        await loggerSendingData({ 
          type: hasNewReserve ? 'newRentCourtSuccessSite' : 'cancelRentCourtSuccessSite', 
          data: requestData,
          state 
        });
      } catch (logError) {
        console.warn('Logger error (non-critical) after success:', logError);
      }
      
      return response.data;
    } catch (error: any) {
      console.log("error fetchCreateCancelRent >>>", error);

      const hasNewReserve = newBasket && Object.keys(newBasket).length > 0;

      try {
        let errorRequestData: any = {};
        try {
          const finalPartnerId = partnerId || getPartnerIdFromStorage() || "d4fc8381-e486-11ea-80c3-ac1f6bb3f7d3";
          const finalPhone = phone || state.authAndReg?.user?.phone || state.authAndReg?.phone || "";
          errorRequestData = prepareCreateCancelRentData(newBasket, cancelBasket, finalPartnerId, finalPhone);
        } catch (prepError) {
          errorRequestData = { newBasket, cancelBasket, partnerId, phone };
        }

        const errorLogType = hasNewReserve 
          ? (error.response?.status >= 500 ? 'newRentCourtFatalErrorSite' : 'newRentCourtErrorSite')
          : 'cancelRentCourtErrorSite';
        
        await loggerSendingData({ 
          type: errorLogType, 
          data: {
            ...errorRequestData,
            error: {
              message: error.message,
              response: error.response?.data,
              status: error.response?.status,
            }
          },
          state 
        });
      } catch (logError) {
        console.warn('Logger error (non-critical) after error:', logError);
      }
      
      return rejectWithValue(error.response?.data || "Ошибка создания/отмены бронирования");
    }
  }
);
